import { useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import { Bug, Lightbulb, Paperclip, X, Trash2, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react-native';

import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { reportesQuery, crearReporte, actualizarEstadoReporte, eliminarReporte } from '@/services/firebase/reportes';
import { subirACloudinary } from '@/services/cloudinary';
import { useAuth } from '@/stores/authStore';
import { typography } from '@/theme/tokens';
import type { Reporte, TipoReporte, EstadoReporte } from '@/types/domain';

// Paleta oscura propia del panel de tickets
const D = {
  bg: '#0B1020',
  panel: '#141B2E',
  panelAlt: '#1B2438',
  border: 'rgba(255,255,255,0.10)',
  text: '#F2F5FA',
  muted: '#94A3B8',
  red: '#E01E37',
  redSoft: 'rgba(224,30,55,0.15)',
  amber: '#F59E0B',
  green: '#10B981',
};

const ESTADO_LABEL: Record<string, string> = {
  nuevo: 'Nuevo',
  en_proceso: 'En proceso',
  resuelto: 'Resuelto',
};
const ESTADO_COLOR: Record<string, string> = {
  nuevo: D.red,
  en_proceso: D.amber,
  resuelto: D.green,
};

export function ReportarBugButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* FAB abajo a la derecha */}
      <Pressable
        onPress={() => setOpen(true)}
        accessibilityLabel="Reportar bug"
        style={({ hovered }) => ({
          // @ts-ignore web only
          position: Platform.OS === 'web' ? 'fixed' : 'absolute',
          right: 22,
          bottom: 22,
          width: 56,
          height: 56,
          borderRadius: 28,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: hovered ? '#C2182E' : D.red,
          // @ts-ignore web only
          boxShadow: '0 8px 24px rgba(224,30,55,0.45)',
          // @ts-ignore web only
          cursor: 'pointer',
          zIndex: 9000,
        })}
      >
        <Bug size={24} color="#fff" />
      </Pressable>

      {open ? <ReporteModal onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function ReporteModal({ onClose }: { onClose: () => void }) {
  const perfil = useAuth((s) => s.perfil);
  const [tab, setTab] = useState<'nuevo' | 'reportados'>('nuevo');
  const { data: reportes, loading } = useFirestoreCollection<Reporte>(reportesQuery);
  // Ticket abierto en detalle (se deriva de la lista en vivo para reflejar cambios de estado).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useMemo(() => reportes.find((r) => r.id === selectedId) ?? null, [reportes, selectedId]);

  const [tipo, setTipo] = useState<TipoReporte>('error');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [files, setFiles] = useState<{ blob: Blob; name: string; preview: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviado, setEnviado] = useState(false);

  const onPickFiles = () => {
    if (Platform.OS !== 'web') return;
    // @ts-ignore web-only
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif';
    input.multiple = true;
    input.onchange = () => {
      const picked = Array.from(input.files ?? []) as File[];
      for (const f of picked) {
        if (files.length >= 5) break;
        if (f.size > 5 * 1024 * 1024) {
          setError(`"${f.name}" supera los 5MB.`);
          continue;
        }
        const preview = URL.createObjectURL(f);
        setFiles((prev) => (prev.length >= 5 ? prev : [...prev, { blob: f, name: f.name, preview }]));
      }
    };
    input.click();
  };

  const quitarFile = (i: number) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const onSubmit = async () => {
    setError(null);
    if (!perfil) return setError('Sesión inválida.');
    if (!titulo.trim()) return setError('Poné un título corto.');
    if (!descripcion.trim()) return setError('Describí el problema.');
    setSaving(true);
    try {
      // subir imágenes
      const urls: string[] = [];
      for (const f of files) {
        const up = await subirACloudinary(f.blob, { folder: 'clinicat/reportes', filename: f.name });
        urls.push(up.url);
      }
      await crearReporte({
        tipo,
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        imagenes: urls,
        autorUid: perfil.uid,
        autorNombre: perfil.nombre,
        autorRol: perfil.rol,
      });
      setEnviado(true);
      setTimeout(() => {
        setTitulo('');
        setDescripcion('');
        setFiles([]);
        setEnviado(false);
        setTab('reportados');
      }, 1400);
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo enviar el reporte.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <View
      style={{
        // @ts-ignore web only
        position: Platform.OS === 'web' ? 'fixed' : 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.55)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 9001,
      }}
    >
      <Pressable style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} onPress={onClose} />
      <View
        style={{
          width: '100%',
          maxWidth: 560,
          maxHeight: '88%',
          backgroundColor: D.bg,
          borderRadius: 16,
          borderWidth: 1,
          borderColor: D.border,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 18, borderBottomWidth: 1, borderBottomColor: D.border }}>
          <View style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: D.redSoft, alignItems: 'center', justifyContent: 'center' }}>
            <Bug size={20} color={D.red} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ color: D.text, fontSize: 17, fontWeight: typography.weights.bold }}>Reportar bug</Text>
            <Text style={{ color: D.muted, fontSize: 12 }}>del sistema de tickets</Text>
          </View>
          <Pressable onPress={onClose} hitSlop={8} style={{ padding: 4 }}>
            <X size={20} color={D.muted} />
          </Pressable>
        </View>

        {/* Tabs */}
        <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: D.border }}>
          {(['nuevo', 'reportados'] as const).map((t) => {
            const active = tab === t;
            return (
              <Pressable
                key={t}
                onPress={() => {
                  setTab(t);
                  setSelectedId(null);
                }}
                style={{
                  flex: 1,
                  paddingVertical: 13,
                  alignItems: 'center',
                  backgroundColor: active ? D.panel : 'transparent',
                  borderBottomWidth: 2,
                  borderBottomColor: active ? D.red : 'transparent',
                  // @ts-ignore web only
                  cursor: 'pointer',
                }}
              >
                <Text style={{ color: active ? D.text : D.muted, fontWeight: typography.weights.bold, fontSize: 12, letterSpacing: 1 }}>
                  {t === 'nuevo' ? 'NUEVO' : 'REPORTADOS'}
                  {t === 'reportados' && reportes.length ? `  ·  ${reportes.length}` : ''}
                </Text>
              </Pressable>
            );
          })}
        </View>

        <ScrollView contentContainerStyle={{ padding: 18, gap: 14 }}>
          {tab === 'nuevo' ? (
            enviado ? (
              <View style={{ alignItems: 'center', paddingVertical: 30, gap: 10 }}>
                <View style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: 'rgba(16,185,129,0.15)', alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ fontSize: 28 }}>✓</Text>
                </View>
                <Text style={{ color: D.text, fontSize: 16, fontWeight: typography.weights.bold }}>¡Reporte enviado!</Text>
                <Text style={{ color: D.muted, fontSize: 13, textAlign: 'center' }}>Gracias. Lo vamos a revisar y corregir.</Text>
              </View>
            ) : (
              <>
                {/* Tipo */}
                <View style={{ flexDirection: 'row', gap: 10 }}>
                  <TipoBtn
                    active={tipo === 'error'}
                    onPress={() => setTipo('error')}
                    icon={<Bug size={15} color={tipo === 'error' ? '#fff' : D.muted} />}
                    label="ERROR"
                    activeColor={D.red}
                  />
                  <TipoBtn
                    active={tipo === 'optimizacion'}
                    onPress={() => setTipo('optimizacion')}
                    icon={<Lightbulb size={15} color={tipo === 'optimizacion' ? '#fff' : D.muted} />}
                    label="OPTIMIZACIÓN"
                    activeColor={D.amber}
                  />
                </View>

                <DarkInput placeholder="Título corto del problema" value={titulo} onChangeText={setTitulo} />
                <DarkInput
                  placeholder="Describí qué pasó, en qué pantalla, cómo reproducirlo…"
                  value={descripcion}
                  onChangeText={setDescripcion}
                  multiline
                  minHeight={110}
                />

                {/* Dropzone */}
                <Pressable
                  onPress={onPickFiles}
                  style={({ hovered }) => ({
                    borderWidth: 1.5,
                    borderStyle: 'dashed',
                    borderColor: hovered ? D.red : D.border,
                    borderRadius: 12,
                    paddingVertical: 22,
                    alignItems: 'center',
                    gap: 6,
                    backgroundColor: hovered ? 'rgba(255,255,255,0.02)' : 'transparent',
                    // @ts-ignore web only
                    cursor: 'pointer',
                  })}
                >
                  <Paperclip size={18} color={D.muted} />
                  <Text style={{ color: D.muted, fontSize: 13 }}>Hacé clic para elegir una captura</Text>
                  <Text style={{ color: D.muted, fontSize: 11, opacity: 0.7 }}>JPG/PNG/GIF · máx 5MB · hasta 5 imágenes</Text>
                </Pressable>

                {files.length > 0 ? (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                    {files.map((f, i) => (
                      <View key={i} style={{ width: 64, height: 64, borderRadius: 8, overflow: 'hidden', borderWidth: 1, borderColor: D.border }}>
                        {/* @ts-ignore web img */}
                        <img src={f.preview} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <Pressable
                          onPress={() => quitarFile(i)}
                          style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: 9, backgroundColor: 'rgba(0,0,0,0.7)', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <X size={11} color="#fff" />
                        </Pressable>
                      </View>
                    ))}
                  </View>
                ) : null}

                {error ? (
                  <View style={{ padding: 11, borderRadius: 10, backgroundColor: D.redSoft }}>
                    <Text style={{ color: '#FCA5A5', fontSize: 13 }}>{error}</Text>
                  </View>
                ) : null}

                <Pressable
                  onPress={onSubmit}
                  disabled={saving}
                  style={({ hovered }) => ({
                    backgroundColor: saving ? '#7a1320' : hovered ? '#F02A43' : D.red,
                    borderRadius: 10,
                    paddingVertical: 14,
                    alignItems: 'center',
                    // @ts-ignore web only
                    cursor: saving ? 'default' : 'pointer',
                  })}
                >
                  <Text style={{ color: '#fff', fontWeight: typography.weights.bold, fontSize: 13, letterSpacing: 1 }}>
                    {saving ? 'ENVIANDO…' : 'ENVIAR REPORTE'}
                  </Text>
                </Pressable>
              </>
            )
          ) : selected ? (
            // DETALLE de un ticket
            <ReporteDetalle
              reporte={selected}
              onBack={() => setSelectedId(null)}
              onEstado={(e) => actualizarEstadoReporte(selected.id, e)}
              onDelete={async () => {
                await eliminarReporte(selected.id);
                setSelectedId(null);
              }}
            />
          ) : (
            // LISTA de tickets
            <View style={{ gap: 10 }}>
              {loading ? (
                <Text style={{ color: D.muted, fontSize: 13, textAlign: 'center', paddingVertical: 20 }}>Cargando…</Text>
              ) : reportes.length === 0 ? (
                <Text style={{ color: D.muted, fontSize: 13, textAlign: 'center', paddingVertical: 30 }}>
                  Todavía no hay reportes. ¡Buena señal! 🐾
                </Text>
              ) : (
                reportes.map((r) => (
                  <Pressable
                    key={r.id}
                    onPress={() => setSelectedId(r.id)}
                    style={({ hovered }) => ({
                      backgroundColor: hovered ? D.panelAlt : D.panel,
                      borderRadius: 12,
                      borderWidth: 1,
                      borderColor: hovered ? 'rgba(255,255,255,0.2)' : D.border,
                      padding: 13,
                      gap: 6,
                      // @ts-ignore web only
                      cursor: 'pointer',
                    })}
                  >
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, backgroundColor: r.tipo === 'error' ? D.redSoft : 'rgba(245,158,11,0.15)' }}>
                        {r.tipo === 'error' ? <Bug size={11} color={D.red} /> : <Lightbulb size={11} color={D.amber} />}
                        <Text style={{ color: r.tipo === 'error' ? '#FCA5A5' : '#FCD34D', fontSize: 10, fontWeight: typography.weights.bold, letterSpacing: 0.5 }}>
                          {r.tipo === 'error' ? 'ERROR' : 'OPTIM.'}
                        </Text>
                      </View>
                      <Text style={{ flex: 1, color: D.text, fontSize: 14, fontWeight: typography.weights.bold }} numberOfLines={1}>
                        {r.titulo}
                      </Text>
                      <View style={{ paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, backgroundColor: (ESTADO_COLOR[r.estado] ?? D.muted) + '22' }}>
                        <Text style={{ color: ESTADO_COLOR[r.estado] ?? D.muted, fontSize: 10, fontWeight: typography.weights.bold }}>
                          {ESTADO_LABEL[r.estado] ?? r.estado}
                        </Text>
                      </View>
                    </View>
                    <Text style={{ color: D.muted, fontSize: 12 }} numberOfLines={2}>
                      {r.descripcion}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Text style={{ color: D.muted, fontSize: 10, opacity: 0.7 }} numberOfLines={1}>
                        {r.autorNombre}{r.autorRol ? ` · ${r.autorRol}` : ''} · {r.creadoEn?.toDate ? r.creadoEn.toDate().toLocaleDateString() : ''}
                      </Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        {r.imagenes?.length ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                            <Paperclip size={11} color={D.muted} />
                            <Text style={{ color: D.muted, fontSize: 10, opacity: 0.7 }}>{r.imagenes.length}</Text>
                          </View>
                        ) : null}
                        <ChevronRight size={15} color={D.muted} />
                      </View>
                    </View>
                  </Pressable>
                ))
              )}
            </View>
          )}
        </ScrollView>
      </View>
    </View>
  );
}

function ReporteDetalle({
  reporte,
  onBack,
  onEstado,
  onDelete,
}: {
  reporte: Reporte;
  onBack: () => void;
  onEstado: (e: EstadoReporte) => void | Promise<void>;
  onDelete: () => void | Promise<void>;
}) {
  const fecha = reporte.creadoEn?.toDate ? reporte.creadoEn.toDate() : null;
  return (
    <View style={{ gap: 14 }}>
      {/* Volver */}
      <Pressable
        onPress={onBack}
        style={({ hovered }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
          alignSelf: 'flex-start',
          opacity: hovered ? 0.8 : 1,
          // @ts-ignore web only
          cursor: 'pointer',
        })}
      >
        <ChevronLeft size={16} color={D.muted} />
        <Text style={{ color: D.muted, fontSize: 13, fontWeight: typography.weights.bold }}>Volver a la lista</Text>
      </Pressable>

      {/* Badges */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 9, paddingVertical: 4, borderRadius: 6, backgroundColor: reporte.tipo === 'error' ? D.redSoft : 'rgba(245,158,11,0.15)' }}>
          {reporte.tipo === 'error' ? <Bug size={12} color={D.red} /> : <Lightbulb size={12} color={D.amber} />}
          <Text style={{ color: reporte.tipo === 'error' ? '#FCA5A5' : '#FCD34D', fontSize: 11, fontWeight: typography.weights.bold, letterSpacing: 0.5 }}>
            {reporte.tipo === 'error' ? 'ERROR' : 'OPTIMIZACIÓN'}
          </Text>
        </View>
        <View style={{ paddingHorizontal: 9, paddingVertical: 4, borderRadius: 999, backgroundColor: (ESTADO_COLOR[reporte.estado] ?? D.muted) + '22' }}>
          <Text style={{ color: ESTADO_COLOR[reporte.estado] ?? D.muted, fontSize: 11, fontWeight: typography.weights.bold }}>
            {ESTADO_LABEL[reporte.estado] ?? reporte.estado}
          </Text>
        </View>
      </View>

      {/* Título completo */}
      <Text style={{ color: D.text, fontSize: 18, fontWeight: typography.weights.bold, lineHeight: 24 }}>
        {reporte.titulo}
      </Text>

      {/* Autor + fecha */}
      <Text style={{ color: D.muted, fontSize: 12 }}>
        {reporte.autorNombre}
        {reporte.autorRol ? ` · ${reporte.autorRol}` : ''}
        {fecha ? ` · ${fecha.toLocaleString()}` : ''}
      </Text>

      {/* Descripción completa (sin truncar, seleccionable) */}
      <View style={{ backgroundColor: D.panel, borderRadius: 12, borderWidth: 1, borderColor: D.border, padding: 14 }}>
        <Text
          // @ts-ignore web only — permitir seleccionar el texto
          selectable
          style={{ color: D.text, fontSize: 14, lineHeight: 21 }}
        >
          {reporte.descripcion}
        </Text>
      </View>

      {/* Capturas */}
      {reporte.imagenes?.length ? (
        <View style={{ gap: 8 }}>
          <Text style={{ color: D.muted, fontSize: 11, fontWeight: typography.weights.bold, letterSpacing: 1 }}>
            CAPTURAS · {reporte.imagenes.length}
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {reporte.imagenes.map((url, i) => (
              <Pressable
                key={i}
                onPress={() => {
                  if (typeof window !== 'undefined') window.open(url, '_blank');
                }}
                style={{
                  width: 92,
                  height: 92,
                  borderRadius: 10,
                  overflow: 'hidden',
                  borderWidth: 1,
                  borderColor: D.border,
                  // @ts-ignore web only
                  cursor: 'pointer',
                }}
              >
                {/* @ts-ignore web img */}
                <img src={url} alt={`captura ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Pressable>
            ))}
          </View>
        </View>
      ) : null}

      {/* Solución — visible para quien reportó */}
      {reporte.solucion ? (
        <View style={{ gap: 6, backgroundColor: 'rgba(16,185,129,0.10)', borderRadius: 12, borderWidth: 1, borderColor: 'rgba(16,185,129,0.35)', padding: 14 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <CheckCircle2 size={14} color={D.green} />
            <Text style={{ color: D.green, fontSize: 11, fontWeight: typography.weights.bold, letterSpacing: 1 }}>SOLUCIÓN</Text>
          </View>
          <Text
            // @ts-ignore web only
            selectable
            style={{ color: D.text, fontSize: 14, lineHeight: 21 }}
          >
            {reporte.solucion}
          </Text>
          {reporte.resueltoPorNombre || reporte.resueltoEn ? (
            <Text style={{ color: D.muted, fontSize: 11 }}>
              {reporte.resueltoPorNombre ?? ''}
              {reporte.resueltoEn?.toDate ? ` · ${reporte.resueltoEn.toDate().toLocaleDateString()}` : ''}
            </Text>
          ) : null}
        </View>
      ) : null}

      {/* Cambiar estado */}
      <View style={{ gap: 8 }}>
        <Text style={{ color: D.muted, fontSize: 11, fontWeight: typography.weights.bold, letterSpacing: 1 }}>MARCAR COMO</Text>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          {(['nuevo', 'en_proceso', 'resuelto'] as EstadoReporte[]).map((e) => {
            const active = reporte.estado === e;
            const c = ESTADO_COLOR[e] ?? D.muted;
            return (
              <Pressable
                key={e}
                onPress={() => {
                  if (!active) onEstado(e);
                }}
                style={({ hovered }) => ({
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 9,
                  alignItems: 'center',
                  backgroundColor: active ? c : hovered ? c + '22' : 'transparent',
                  borderWidth: 1,
                  borderColor: active ? c : D.border,
                  // @ts-ignore web only
                  cursor: active ? 'default' : 'pointer',
                })}
              >
                <Text style={{ color: active ? '#0B1020' : c, fontSize: 12, fontWeight: typography.weights.bold }}>
                  {ESTADO_LABEL[e]}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      {/* Eliminar */}
      <Pressable
        onPress={() => {
          const ok =
            typeof window !== 'undefined' && typeof window.confirm === 'function'
              ? window.confirm('¿Eliminar este reporte? No se puede deshacer.')
              : true;
          if (ok) onDelete();
        }}
        style={({ hovered }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          paddingVertical: 11,
          borderRadius: 9,
          backgroundColor: hovered ? D.redSoft : 'transparent',
          borderWidth: 1,
          borderColor: D.redSoft,
          // @ts-ignore web only
          cursor: 'pointer',
        })}
      >
        <Trash2 size={14} color={D.red} />
        <Text style={{ color: D.red, fontSize: 12, fontWeight: typography.weights.bold }}>Eliminar reporte</Text>
      </Pressable>
    </View>
  );
}

function TipoBtn({ active, onPress, icon, label, activeColor }: { active: boolean; onPress: () => void; icon: React.ReactNode; label: string; activeColor: string }) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        paddingVertical: 12,
        borderRadius: 10,
        backgroundColor: active ? activeColor : 'transparent',
        borderWidth: 1,
        borderColor: active ? activeColor : D.border,
        // @ts-ignore web only
        cursor: 'pointer',
      }}
    >
      {icon}
      <Text style={{ color: active ? '#fff' : D.muted, fontWeight: typography.weights.bold, fontSize: 12, letterSpacing: 0.5 }}>{label}</Text>
    </Pressable>
  );
}

function DarkInput({
  placeholder,
  value,
  onChangeText,
  multiline,
  minHeight,
}: {
  placeholder: string;
  value: string;
  onChangeText: (t: string) => void;
  multiline?: boolean;
  minHeight?: number;
}) {
  return (
    <TextInput
      placeholder={placeholder}
      placeholderTextColor={D.muted}
      value={value}
      onChangeText={onChangeText}
      multiline={multiline}
      style={{
        backgroundColor: D.panelAlt,
        borderWidth: 1,
        borderColor: D.border,
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 12,
        color: D.text,
        fontSize: 14,
        fontFamily: typography.body.fontFamily,
        minHeight: minHeight ?? undefined,
        textAlignVertical: multiline ? 'top' : 'center',
        // @ts-ignore web only
        outlineStyle: 'none',
      }}
    />
  );
}
